import { FieldErrors, FieldValues } from 'react-hook-form'
import get from 'lodash/get'
import { TCreateShipmentSchema } from '../schema'
import { TAutocompleteValueSchema } from '../schema/AutocompleteValueSchema'
import { TShipmentSchema } from '../schema/ShipmentSchema'
import { TStopSchema } from '../schema/ShipmentSchema/StopSchema'

/**
 * Returns the error message from a field in the top level of CreateShipmentSchema
 *
 * @param errors `{ equipment: { id: 'required', message: 'This field is required' } }`
 * @param keyName 'equipment'
 * @returns 'This field is required'
 */
export const getErrorMessage = <T extends FieldErrors<TCreateShipmentSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string => {
  return get(errors, [keyName, 'message'])
}

/**
 * Returns the error message from a field in EqiupmentAttributesSchema
 */
export const getEquipmentAttributeErrorMessage = <S extends FieldValues>(
  errors: FieldErrors<S> | undefined,
  keyName: keyof FieldErrors<S>
): string | undefined => {
  if (errors) {
    return get(errors, [keyName, 'message'])
  }
  return undefined
}

/**
 * Returns the error message from a field in AutocompleteValueSchema
 *
 * @param errors `{ autocompleteValueId: { id: 'required', message: 'This field is required' } }`
 * @param keyName 'autocompleteValueId'
 * @returns 'This field is required'
 */
export const getAutocompleteErrorMessage = <
  T extends FieldErrors<TAutocompleteValueSchema>,
  K extends keyof T
>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

/**
 * Returns the error message from a field in ShipmentSchema
 *
 * @param errors `{ opsRepId: { id: 'required', message: 'This field is required' } }`
 * @param keyName 'opsRepId'
 * @returns 'This field is required'
 */
export const getShipmentErrorMessage = <T extends FieldErrors<TShipmentSchema>, K extends keyof T>(
  errors: T | undefined,
  keyName: K
): string => {
  return get(errors, [keyName, 'message'])
}

/**
 * Returns the FieldErrors from a specific stop in ShipmentSchema
 *
 * @param errors `{ stops: [{ startAtDate: { id: 'date-in-future', message: 'Date must be in the future' } }] }`
 * @param stopIndex 0
 * @returns `{ startAtDate: { id: 'date-in-future', message: 'Date must be in the future' } }`
 */
export const getShipmentStopFieldError = <T extends FieldErrors<TShipmentSchema>>(
  errors: T | undefined,
  stopIndex: number
): FieldErrors<TStopSchema> | undefined => {
  if (errors) {
    return get(errors.stops, stopIndex)
  }
  return undefined
}

/**
 * Returns the error message from a specific stop in ShipmentSchema
 *
 * @param errors `{ stops: [{ startAtDate: { id: 'date-in-future', message: 'Date must be in the future' } }] }`
 * @param stopIndex 0
 * @returns 'Date must be in the future'
 */
export const getShipmentStopErrorMessage = <T extends FieldErrors<TShipmentSchema>>(
  errors: T | undefined,
  stopIndex: number
): string | undefined => {
  if (errors) {
    return get(getShipmentStopFieldError(errors, stopIndex), 'message')
  }
  return undefined
}

/**
 * Return the error message from a field in StopSchema
 * @param errors `{ startAtDate: { id: 'date-in-future', message: 'Date must be in the future' } }`
 * @param keyName 'startAtDate'
 * @returns 'Date must be in the future'
 */
export const getStopErrorMessage = <T extends FieldErrors<TStopSchema>, K extends keyof T>(
  errors: T | undefined,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}
